/**
 * ms-armar-paquete-v2 — elegir N productos de una colección para un paquete de
 * precio fijo, pensado para el celular. Lo usa sections/ms-paquete-v2.liquid.
 * La v1 (ms-armar-paquete.js) sigue intacta para ms-paquete.liquid.
 *
 * LO QUE HACE DISTINTO A LA V1
 * · En pantallas de una columna (< 990 px) la lista vive en una HOJA (<dialog>
 *   modal) que sube desde abajo. Al abrir, el nodo de la lista se MUDA a la hoja
 *   (conserva sus eventos y su estado); al cerrar regresa a su lugar. Así no hay
 *   dos listas que sincronizar.
 * · Nada queda muerto: las casillas, el botón de compra y el de la barra fija
 *   abren la hoja mientras falten aromas. El botón de compra NO se deshabilita:
 *   cambia su texto («Elige tus 4» / «Elige 2 más») y, al tocarlo incompleto, se
 *   intercepta antes que el tema (captura en window, que corre antes que la
 *   delegación de Horizon, registrada en document).
 * · Con el paquete completo, el botón de la hoja cierra la hoja y aprieta el
 *   botón real: el tema agrega, abre el carrito y todo sigue su camino normal.
 *
 * COMO VIAJA LA ELECCIÓN AL PEDIDO: igual que la v1, inputs ocultos
 * properties[<prefijo><n>] dentro del form de /cart/add de ESTA sección.
 */
if (!customElements.get('ms-armar-paquete-v2')) {
  class MsArmarPaqueteV2 extends HTMLElement {
    connectedCallback() {
      if (!this.listo) {
        this.listo = true;
        this.iniciar();
      }
      this.engancharVentana();
    }

    /**
     * El editor de Shopify vuelve a pintar la sección con cada ajuste: la instancia vieja
     * se desconecta, pero sus oyentes en window seguían vivos y, con la casilla incompleta,
     * se tragaban el clic de la barra fija y dejaban la página sin scroll (7-oct-2026).
     */
    disconnectedCallback() {
      this.soltarVentana();
      if (this.bloqueada) this.desbloquear();
    }

    iniciar() {
      const d = this.dataset;
      this.cantidad = parseInt(d.cantidad, 10) || 4;
      this.prefijo = d.prefijo || 'select-';
      this.inicio = parseInt(d.inicio, 10) || 1;
      this.textoVacio = d.vacio || 'Elige el [i]';
      this.repetir = d.repetir === 'true';
      this.precio = d.precio || '';
      this.txtElegir = (d.botonElegir || 'Elige tus [n]').replace('[n]', this.cantidad);
      this.txtFaltan = d.botonFaltan || 'Elige [faltan] más';
      this.txtHojaFaltan = d.hojaFaltan || 'Te faltan [faltan]';
      this.txtHojaListo = d.hojaListo || 'Agregar al carrito · [precio]';

      this.elegidos = [];
      this.filtro = '';
      this.items = Array.from(this.querySelectorAll('[data-ms-item]'));
      this.slots = Array.from(this.querySelectorAll('[data-ms-slot]'));
      this.minis = Array.from(this.querySelectorAll('[data-ms-mini]'));
      this.buscador = this.querySelector('[data-ms-buscador]');
      this.contadores = Array.from(this.querySelectorAll('[data-ms-contador]'));
      this.aviso = this.querySelector('[data-ms-aviso]');
      this.vacio = this.querySelector('[data-ms-vacio]');
      this.elegir = this.querySelector('[data-ms-elegir]');
      this.hoja = this.querySelector('[data-ms-hoja]');
      this.hojaCuerpo = this.querySelector('[data-ms-hoja-cuerpo]');
      this.hojaCta = this.querySelector('[data-ms-hoja-cta]');
      this.filtros = Array.from(this.querySelectorAll('[data-ms-filtro]'));
      this.unaColumna = window.matchMedia('(max-width: 989px)');

      // Marca de dónde vive la lista, para regresarla al cerrar la hoja.
      if (this.elegir) {
        this.casa = document.createComment('ms-pq2-lista');
        this.elegir.before(this.casa);
      }

      this.lista = this.querySelector('[data-ms-lista]');
      this.fotos = new Map();
      this.items.forEach((li) => this.conectarItem(li));

      this.slots.forEach((slot) => {
        slot.querySelector('[data-ms-abrir]')?.addEventListener('click', () => this.abrir());
      });
      this.querySelector('[data-ms-abrir-buscar]')?.addEventListener('click', () => this.abrir({ buscar: true }));

      this.filtros.forEach((b) =>
        b.addEventListener('click', () => {
          this.filtro = b.dataset.msFiltro || '';
          this.filtros.forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
          this.filtrar();
        })
      );

      if (this.buscador) {
        this.buscador.addEventListener('input', () => this.filtrar());
        this.buscador.addEventListener('focus', () => this.cargarResto(), { once: true });
      }

      if (this.hoja) {
        this.querySelector('[data-ms-cerrar]')?.addEventListener('click', () => this.cerrar());
        // Tocar fuera de la hoja (el fondo oscuro) la cierra.
        this.hoja.addEventListener('click', (e) => {
          if (e.target === this.hoja) this.cerrar();
        });
        this.hoja.addEventListener('close', () => this.alCerrar());
        this.hojaCta?.addEventListener('click', () => this.ctaHoja());
        this.arrastre();
      }

      this.unaColumna.addEventListener?.('change', () => {
        if (!this.unaColumna.matches && this.hoja?.open) this.cerrar();
      });

      this.conectarBotones();
      this.desdeLiga();
      this.pintar();

      // Lo que no cupo en las 250 del primer pintado llega después de cargar la página.
      const despues = () => setTimeout(() => this.cargarResto(), 300);
      if (document.readyState === 'complete') despues();
      else window.addEventListener('load', despues, { once: true });
    }

    conectarItem(li) {
      this.fotos.set(li.dataset.nombre, li.dataset.img || '');
      li.querySelector('.ms-pq2__pick')?.addEventListener('click', () => this.agregar(li));
      li.querySelector('[data-ms-menos]')?.addEventListener('click', () => this.quitarUno(li.dataset.nombre));
    }

    /**
     * Un for de Liquid sin paginate corta en 50 (Perfumara, 21-oct-2026: Dama traía 50 de 385
     * y la gente creía que ya no había variedad). La sección pinta 250 y aquí se piden las
     * demás páginas a ESTA misma sección (Section Rendering API + ?page=N). Una sola vez.
     */
    cargarResto() {
      if (this.carga) return this.carga;
      const paginas = parseInt(this.lista?.dataset.msPaginas, 10) || 1;
      const seccion = this.dataset.seccion;
      if (paginas < 2 || !seccion || !this.lista) {
        this.carga = Promise.resolve();
        return this.carga;
      }
      this.cargando = true;
      this.carga = (async () => {
        const vistos = new Set(this.items.map((li) => li.dataset.handle));
        for (let pagina = 2; pagina <= paginas; pagina++) {
          let html = '';
          try {
            const url = new URL(location.href);
            url.searchParams.set('section_id', seccion);
            url.searchParams.set('page', String(pagina));
            const r = await fetch(url);
            if (!r.ok) break;
            html = await r.text();
          } catch {
            break;
          }
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const nuevos = [];
          doc.querySelectorAll('[data-ms-lista] > [data-ms-item]').forEach((li) => {
            // El orden «más vendidos» puede moverse entre una página y otra: sin repetidos.
            if (vistos.has(li.dataset.handle)) return;
            vistos.add(li.dataset.handle);
            const nodo = document.importNode(li, true);
            this.conectarItem(nodo);
            nuevos.push(nodo);
          });
          this.lista.append(...nuevos);
          this.items.push(...nuevos);
          this.pintar();
          this.filtrar();
        }
        this.cargando = false;
        this.querySelectorAll('[data-ms-total]').forEach((t) => (t.textContent = String(this.items.length)));
        this.desdeLiga();
        this.pintar();
        this.filtrar();
      })();
      return this.carga;
    }

    /**
     * ?aroma=<handle> (lo manda ms-paquete-link desde la ficha del perfume, 30-sep-2026):
     * ese aroma entra en la casilla 1. Solo si está en la lista del paquete y una vez.
     */
    desdeLiga() {
      let handle = '';
      try {
        handle = new URLSearchParams(location.search).get('aroma') || '';
      } catch {
        return;
      }
      if (!handle || this.elegidos.length) return;
      const li = this.items.find((x) => x.dataset.handle === handle);
      if (li) this.elegidos.push(li.dataset.nombre);
    }

    // ── Abrir / cerrar ───────────────────────────────────────────────────
    /** @param {{buscar?: boolean}} [opciones] buscar: viene del atajo con lupa, el teclado sí se abre. */
    abrir(opciones = {}) {
      if (!this.elegir) return;
      this.cargarResto();

      // Dos columnas: la lista ya está a la vista. Se lleva al cliente hasta ella.
      if (!this.unaColumna.matches || !this.hoja || typeof this.hoja.showModal !== 'function') {
        this.elegir.scrollIntoView({ behavior: 'smooth', block: 'start' });
        this.buscador?.focus({ preventScroll: true });
        return;
      }

      if (this.hoja.open) return;
      this.hojaCuerpo.append(this.elegir);
      this.hoja.showModal();
      this.bloquear();
      this.hojaCuerpo.scrollTop = 0;
      // Sin enfocar el buscador: en iOS abre el teclado y tapa la mitad de la hoja.
      // Salvo que lo pidió tocando la lupa: entonces el teclado es lo que espera.
      if (opciones.buscar && this.buscador) this.buscador.focus({ preventScroll: true });
      else this.hoja.querySelector('[data-ms-cerrar]')?.focus({ preventScroll: true });
    }

    cerrar() {
      if (this.hoja?.open) this.hoja.close();
    }

    alCerrar() {
      this.desbloquear();
      this.hoja.style.transform = '';
      if (this.casa && this.elegir) this.casa.after(this.elegir);
    }

    /**
     * Sin scroll detrás de la hoja. Con barra de scroll clásica (Windows, Mac con mouse,
     * el editor de Shopify) quitarla ensanchaba la página 15 px y todo brincaba al abrir y
     * al cerrar (Leo, 7-oct-2026: «cuando se cierra el pop up como que se mueve»). Se deja
     * ese mismo margen mientras la hoja está abierta; con barra flotante (celular,
     * trackpad) mide 0 y no se toca nada.
     */
    bloquear() {
      const html = document.documentElement;
      const barra = window.innerWidth - html.clientWidth;
      if (barra > 0) html.style.paddingInlineEnd = barra + 'px';
      html.classList.add('ms-pq2-abierta');
      this.bloqueada = true;
    }

    desbloquear() {
      const html = document.documentElement;
      html.classList.remove('ms-pq2-abierta');
      html.style.paddingInlineEnd = '';
      this.bloqueada = false;
    }

    /** Deslizar la hoja hacia abajo desde su cabecera la cierra. */
    arrastre() {
      const asa = this.hoja.querySelector('[data-ms-hoja-asa]');
      if (!asa) return;
      let y0 = null;
      let dy = 0;
      asa.addEventListener('touchstart', (e) => {
        y0 = e.touches[0].clientY;
        dy = 0;
      }, { passive: true });
      asa.addEventListener('touchmove', (e) => {
        if (y0 === null) return;
        dy = Math.max(0, e.touches[0].clientY - y0);
        this.hoja.style.transform = `translateY(${dy}px)`;
      }, { passive: true });
      asa.addEventListener('touchend', () => {
        if (y0 === null) return;
        y0 = null;
        if (dy > 90) this.cerrar();
        else this.hoja.style.transform = '';
      });
    }

    // ── Botones de compra (el de la ficha y el de la barra fija) ──────────
    conectarBotones() {
      this.form = this.querySelector('form[action*="/cart/add"]');
      this.boton = this.form?.querySelector('[ref="addToCartButton"], .add-to-cart-button, button[type="submit"][name="add"]') || null;
      this.textoBoton = this.textoDe(this.boton);

      // Captura en window: corre antes que la delegación de Horizon (document).
      // Se engancha al conectar y se suelta al desconectar (ver disconnectedCallback).
      this.alClicVentana = (e) => {
        if (!this.isConnected || this.completo()) return;
        const t = e.target instanceof Element ? e.target : null;
        if (!t) return;
        const enBoton = this.boton && this.boton.contains(t);
        const enBarra = t.closest('[data-sticky-add-to-cart-button]');
        if (!enBoton && !enBarra) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        this.abrir();
        this.sacudir();
      };

      this.alEnviarVentana = (e) => {
        if (!this.isConnected || e.target !== this.form || this.completo()) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        this.abrir();
      };
    }

    engancharVentana() {
      if (this.enganchada || !this.alClicVentana) return;
      window.addEventListener('click', this.alClicVentana, true);
      window.addEventListener('submit', this.alEnviarVentana, true);
      this.enganchada = true;
    }

    soltarVentana() {
      if (!this.enganchada) return;
      window.removeEventListener('click', this.alClicVentana, true);
      window.removeEventListener('submit', this.alEnviarVentana, true);
      this.enganchada = false;
    }

    /** El texto visible del botón: el <span> más adentro de add-to-cart-text__content. */
    spanTexto(boton) {
      if (!boton) return null;
      let el = boton.querySelector('.add-to-cart-text__content');
      while (el && el.firstElementChild && el.firstElementChild.tagName === 'SPAN') el = el.firstElementChild;
      return el;
    }

    textoDe(boton) {
      return this.spanTexto(boton)?.textContent.trim() || '';
    }

    ponerTexto(boton, texto) {
      const span = this.spanTexto(boton);
      if (span && span.textContent.trim() !== texto) span.textContent = texto;
    }

    ctaHoja() {
      if (!this.completo()) return this.sacudir();
      this.cerrar();
      // El botón real, ya completo: pasa por el flujo normal del tema.
      requestAnimationFrame(() => this.boton?.click());
    }

    completo() {
      return this.elegidos.length === this.cantidad;
    }

    // ── Buscar y filtrar ─────────────────────────────────────────────────
    filtrar() {
      const q = this.normalizar(this.buscador?.value);
      const f = this.normalizar(this.filtro);
      let visibles = 0;

      this.items.forEach((li) => {
        const texto = this.normalizar(li.dataset.buscar);
        const hay = (!q || texto.includes(q)) && (!f || texto.includes(f));
        li.hidden = !hay;
        if (hay) visibles++;
      });

      // Mientras llegan las demás páginas, «no encontramos» todavía no es cierto.
      if (this.vacio) this.vacio.hidden = visibles > 0 || Boolean(this.cargando);
    }

    /** Sin acentos y en minúsculas: "citrica" encuentra "Cítrica". */
    normalizar(s) {
      return (s || '').toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '').trim();
    }

    // ── Elegir ───────────────────────────────────────────────────────────
    agregar(li) {
      const nombre = li.dataset.nombre;
      if (this.repetir) {
        if (this.completo()) return this.sacudir();
        this.elegidos.push(nombre);
      } else {
        const i = this.elegidos.indexOf(nombre);
        if (i > -1) this.elegidos.splice(i, 1);
        else {
          if (this.completo()) return this.sacudir();
          this.elegidos.push(nombre);
        }
      }
      this.pintar();
    }

    /** «−» en la tarjeta: quita la ÚLTIMA vez que se eligió ese aroma. */
    quitarUno(nombre) {
      const i = this.elegidos.lastIndexOf(nombre);
      if (i > -1) {
        this.elegidos.splice(i, 1);
        this.pintar();
      }
    }

    /** Quita la casilla N, no "la primera que se llame así". */
    quitarEn(indice) {
      if (indice < 0 || indice >= this.elegidos.length) return;
      this.elegidos.splice(indice, 1);
      this.pintar();
    }

    sacudir() {
      if (!this.aviso) return;
      if (!this.completo()) return;
      this.aviso.classList.remove('ms-pq2__aviso--activo');
      void this.aviso.offsetWidth;
      this.aviso.classList.add('ms-pq2__aviso--activo');
    }

    // ── Dibujar ──────────────────────────────────────────────────────────
    pintar() {
      const n = this.elegidos.length;
      const completo = n === this.cantidad;
      const faltan = this.cantidad - n;

      this.items.forEach((li) => {
        const veces = this.elegidos.filter((x) => x === li.dataset.nombre).length;
        li.classList.toggle('ms-pq2__item--elegido', veces > 0);
        li.classList.toggle('ms-pq2__item--varios', veces > 1);
        const marca = li.querySelector('[data-ms-veces]');
        if (marca) marca.textContent = veces > 1 ? '×' + veces : '';
        li.querySelector('.ms-pq2__pick')?.setAttribute('aria-pressed', veces > 0 ? 'true' : 'false');
        const menos = li.querySelector('[data-ms-menos]');
        if (menos) menos.hidden = veces === 0;
      });

      this.contadores.forEach((c) => {
        c.textContent = n + ' de ' + this.cantidad;
        c.classList.toggle('ms-pq2__contador--listo', completo);
      });

      // «Ya elegiste 4. Quita uno…» solo mientras está lleno: al quitar uno se quedaba a la vista.
      if (!completo) this.aviso?.classList.remove('ms-pq2__aviso--activo');

      this.slots.forEach((slot, i) => this.pintarCasilla(slot, i));
      this.minis.forEach((mini, i) => this.pintarMini(mini, i));

      if (this.hojaCta) {
        this.hojaCta.textContent = completo
          ? this.txtHojaListo.replace('[precio]', this.precio)
          : this.txtHojaFaltan.replace('[faltan]', faltan);
        this.hojaCta.setAttribute('aria-disabled', completo ? 'false' : 'true');
      }

      // Texto del botón de compra y del de la barra fija.
      const texto = completo
        ? this.textoBoton || 'Agregar al carrito'
        : n === 0
          ? this.txtElegir
          : this.txtFaltan.replace('[faltan]', faltan);
      this.ponerTexto(this.boton, texto);
      document
        .querySelectorAll('[data-sticky-add-to-cart-button]')
        .forEach((b) => this.ponerTexto(b, texto));

      this.sincronizarForm();
    }

    pintarCasilla(slot, i) {
      const nombre = this.elegidos[i];
      const cuerpo = slot.querySelector('[data-ms-slot-cuerpo]');
      if (!cuerpo) return;

      slot.classList.toggle('ms-pq2__slot--lleno', Boolean(nombre));
      slot.querySelector('.ms-pq2__slot-x')?.remove();

      if (!nombre) {
        const vacio = this.crear('span', 'ms-pq2__slot-vacio', '');
        vacio.append(this.crear('span', '', this.textoVacio.replace('[i]', i + 1)));
        const mas = this.crear('span', 'ms-pq2__slot-mas', '');
        mas.setAttribute('aria-hidden', 'true');
        mas.innerHTML =
          '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>';
        vacio.append(mas);
        cuerpo.replaceChildren(vacio);
        return;
      }

      const partes = [];
      const foto = this.fotos.get(nombre);
      if (foto) {
        const img = document.createElement('img');
        img.className = 'ms-pq2__slot-foto';
        img.src = foto;
        img.alt = '';
        partes.push(img);
      }
      partes.push(this.crear('span', 'ms-pq2__slot-nombre', nombre));
      cuerpo.replaceChildren(...partes);

      // La × va FUERA del botón de la casilla (no se anida un botón en otro).
      const x = document.createElement('button');
      x.type = 'button';
      x.className = 'ms-pq2__slot-x';
      x.setAttribute('aria-label', 'Quitar ' + nombre);
      x.innerHTML =
        '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>';
      x.addEventListener('click', () => this.quitarEn(i));
      slot.append(x);
    }

    pintarMini(mini, i) {
      const nombre = this.elegidos[i];
      mini.classList.toggle('ms-pq2__mini-slot--lleno', Boolean(nombre));
      if (!nombre) {
        mini.replaceChildren(this.crear('span', 'ms-pq2__mini-num', String(i + 1)));
        return;
      }
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'ms-pq2__mini-quitar';
      b.setAttribute('aria-label', 'Quitar ' + nombre);
      const foto = this.fotos.get(nombre);
      if (foto) {
        const img = document.createElement('img');
        img.src = foto;
        img.alt = '';
        b.append(img);
      } else {
        b.textContent = String(i + 1);
      }
      b.addEventListener('click', () => this.quitarEn(i));
      mini.replaceChildren(b);
    }

    crear(tag, clase, texto) {
      const el = document.createElement(tag);
      if (clase) el.className = clase;
      el.textContent = texto;
      return el;
    }

    // ── Puente con el formulario de compra ───────────────────────────────
    sincronizarForm() {
      const form = this.form || this.querySelector('form[action*="/cart/add"]');
      if (!form) return;

      form.querySelectorAll('[data-ms-pack-prop]').forEach((el) => el.remove());
      this.elegidos.forEach((nombre, idx) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[' + this.prefijo + (this.inicio + idx) + ']';
        input.value = nombre;
        input.setAttribute('data-ms-pack-prop', '');
        form.appendChild(input);
      });
    }
  }

  customElements.define('ms-armar-paquete-v2', MsArmarPaqueteV2);
}
